import { Fragment, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import AiRail, { useAssistantOpen } from './AiRail';
import RecordlessLoiHost from './RecordlessLoiHost';
import RecordlessProposalHost from './RecordlessProposalHost';
import { AssistantRailContext, useAssistantRailState } from '../contexts/AssistantRailContext';
import { useMorningChat } from '../contexts/MorningChatContext';
import CommandPalette from './CommandPalette';
import InstallPrompt from './InstallPrompt';
import BackButton from './BackButton';
import PhoneTabBar from './PhoneTabBar';
import { useCommandPalette } from '../hooks/useCommandPalette';
import { NAV_GROUPS } from './navigation';
import type { NavCounts } from '../types';

/**
 * THE SHELL (OO1, 2026-09-03) - built to his Claude Design project
 * (docs/planning/ui-update-claude-design-spec.md, section 3a):
 *   - a light sidebar, 246px, grouped and collapsible, that the hamburger folds to a 64px icon
 *     rail; the active item carries a 3px KW-red inset bar and a red icon;
 *   - a 60px top bar on every width with the search box that opens the ⌘K palette and the
 *     "● Assistant" toggle that owns the AI rail's open state;
 *   - the sidebar footer: "A KW Commercial affiliate" and the user block;
 *   - no page footer.
 * The mobile drawer stays (the phone is his look); the rail is desktop only.
 *
 * Z-ORDER, kept from the previous shell because AiRail.test.tsx pins it: the top bar is
 * z-[1100] below md (the rail's sheet, z-[1200], must cover it) and z-20 from md up so page
 * modals (z-50) still cover the bar; the drawer overlay is z-[2100], above the sheet.
 */

const SIDEBAR_KEY = 'apex-sidebar-collapsed';
const GROUPS_KEY = 'apex-nav-groups';
/** The top bar's height. The docked rail and the fullBleed map both subtract it. */
export const TOP_BAR_PX = 60;

function readGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* fall through to the defaults */ }
  return Object.fromEntries(NAV_GROUPS.map((g) => [g.key, g.defaultOpen]));
}

const Hamburger = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

const SignOutIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
);

/** "JL" from a name, or from the email's local part when there is no name. */
function initialsOf(name: string | undefined, email: string | undefined): string {
  const src = (name && name.trim()) || (email ? email.split('@')[0].replace(/[._-]+/g, ' ') : '');
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export default function Layout({ children, fullBleed = false }: { children: React.ReactNode; fullBleed?: boolean }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });
  const [groupsOpen, setGroupsOpen] = useState<Record<string, boolean>>(readGroups);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [navCounts, setNavCounts] = useState<NavCounts | null>(null);
  const palette = useCommandPalette(); // global Cmd+K / Ctrl+K listener
  const assistant = useAssistantOpen(); // the rail's open state, owned here so the top bar can toggle it
  /**
   * MORNING2 (Task 9): on /morning, when the page holds the chat itself, there is no rail - one chat, one list on the
   *   screen - even when the rail's remembered open state is true (it is kept, for the next page). The top bar's Assistant
   *   then goes to the page's chat box, and so does a letter asking for the chat.
   */
  const { focusChat } = useMorningChat();
  const pageChat = location.pathname === '/morning' ? focusChat : undefined;
  const { setOpen: setAssistantOpen } = assistant;
  const railSetOpen = useCallback((next: boolean) => {
    if (next && pageChat) pageChat();
    else setAssistantOpen(next);
  }, [pageChat, setAssistantOpen]);
  /** ⭐ The 112th: the chat usable BESIDE an open letter - the rail's side of it, owned with the rail's open state. */
  const { rail, railBesideLetter } = useAssistantRailState({ open: assistant.open && !pageChat, setOpen: railSetOpen, fullBleed });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Persist collapse state
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  useEffect(() => {
    try { localStorage.setItem(GROUPS_KEY, JSON.stringify(groupsOpen)); } catch {}
  }, [groupsOpen]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Poll the unread conversation count for the nav badge. This layout mounts on every
  // authenticated page, so a failed request must degrade quietly (unread just stays at its
  // last known value) rather than throwing - an uncaught rejection here would be an
  // app-wide failure, not a page-local one. 60s is plenty for a two-person tool.
  useEffect(() => {
    const load = () => api.getUnreadCount().then((r) => setUnread(r.count)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // OO1a: the sidebar's four counts, on the same quiet-failure terms as unread above - a
  // rejection here would be app-wide, not page-local. Re-read on every route change as well
  // as on the timer, so creating a deal or clearing a flag is reflected without a reload.
  useEffect(() => {
    let cancelled = false;
    const load = () => api.getNavCounts()
      .then((r) => { if (!cancelled) setNavCounts(r.counts); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path.startsWith('/crm')) return location.pathname.startsWith(path);
    if (path === '/asset-services') return location.pathname === '/asset-services';
    if (path.startsWith('/asset-services/')) return location.pathname.startsWith(path);
    // A record page lights its list's item, as the design shows the property record under
    // "Property Database".
    if (path === '/data-hub') return location.pathname.startsWith('/data-hub');
    return location.pathname === path;
  };

  const toggleGroup = (key: string) => setGroupsOpen((g) => ({ ...g, [key]: !g[key] }));

  /**
   * OO1a. Two resolvers, used by BOTH the expanded sidebar and the collapsed rail, so the two
   * renderings cannot drift - the rail's dot used to hard-code `/threads` and would have
   * missed Needs Attention entirely.
   *
   * ⭐ A count is only shown when it is a NUMBER. `/nav-counts` returns null for any source it
   * could not read, and null renders as nothing: a wrong number beside a link is worse than no
   * number, because it is believed. Zero is a real answer and IS shown for a count; an alert at
   * zero is not an alert, so it is not.
   */
  const alertFor = (path: string): number | null => {
    if (path === '/threads') return unread > 0 ? unread : null;
    if (path === '/crm/needs-attention') {
      const n = navCounts?.needs_attention;
      return typeof n === 'number' && n > 0 ? n : null;
    }
    return null;
  };

  const countFor = (path: string): number | null => {
    const n =
      path === '/dashboard' ? navCounts?.my_deals :
      path === '/data-hub' ? navCounts?.property_database :
      path === '/crm/deals' ? navCounts?.deals :
      undefined;
    return typeof n === 'number' ? n : null;
  };

  const u = user as { email?: string; full_name?: string } | null;
  const displayName = u?.full_name || (u?.email ? u.email.split('@')[0] : 'Account');
  const initials = initialsOf(u?.full_name, u?.email);

  /** The expanded sidebar - the desktop column, and the phone's drawer. */
  const renderFullSidebar = (isMobile: boolean) => (
    <div className="flex h-full flex-col">
      <div className="flex h-[60px] shrink-0 items-center gap-2 border-b border-gray-150 pl-2 pr-3">
        <button
          type="button"
          onClick={() => (isMobile ? setMobileOpen(false) : setCollapsed(true))}
          aria-label={isMobile ? 'Close navigation' : 'Collapse navigation'}
          title={isMobile ? 'Close navigation' : 'Collapse navigation'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-900 hover:bg-gray-100"
        >
          <Hamburger />
        </button>
        <Link to="/dashboard" className="flex items-center">
          <img src="/apex-logo.png" alt="Apex Real Estate Services" className="block h-6 w-auto" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 pb-2 pt-3.5" aria-label="Main">
        {NAV_GROUPS.map((group) => {
          const holdsActive = group.items.some((i) => isActive(i.path));
          const open = groupsOpen[group.key] || holdsActive;
          return (
            <div key={group.key} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={open}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-[7px] text-[10px] font-bold uppercase leading-none tracking-[.14em] text-gray-500 hover:text-gray-800"
              >
                <span>{group.title}</span>
                <svg
                  width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
                  className={`ml-auto opacity-70 transition-transform ${open ? '' : '-rotate-90'}`}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {open && (
                <div className="flex flex-col gap-px">
                  {group.items.map((item) => {
                    const active = isActive(item.path);
                    // OO1a: the design shows TWO kinds of right-aligned number, and the
                    // difference is meaning, not decoration. A plain COUNT (`600 10.5px`,
                    // #828282) says how much is behind the link; an ALERT (`700 10px`, white
                    // on KW red, fully round) says something is waiting for you. Needs
                    // Attention and unread threads are alerts; the other three are counts.
                    const alert = alertFor(item.path);
                    const count = alert === null ? countFor(item.path) : null;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        aria-current={active ? 'page' : undefined}
                        className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] leading-none transition-colors ${
                          active
                            ? 'bg-gray-100 font-semibold text-gray-800 shadow-[inset_3px_0_0_#CE011F] [&>svg]:text-primary-500'
                            : 'font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 [&>svg]:opacity-80'
                        }`}
                      >
                        {item.icon}
                        <span className="truncate">{item.label}</span>
                        {alert !== null && (
                          <span className="ml-auto shrink-0 rounded-full bg-primary-500 px-1.5 py-[3px] text-[10px] font-bold leading-none text-white">
                            {alert.toLocaleString()}
                          </span>
                        )}
                        {count !== null && (
                          <span className="ml-auto shrink-0 text-[10.5px] font-semibold leading-none text-gray-500 tabular-nums">
                            {count.toLocaleString()}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-gray-150 px-3.5 py-[11px] text-[12px] font-semibold uppercase leading-none tracking-[.1em] text-gray-400">
        A KW Commercial affiliate
      </div>
      <div className="flex shrink-0 items-center gap-2.5 border-t border-gray-150 px-3.5 py-3">
        <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-gray-800 text-[12px] font-bold leading-none text-white">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-semibold leading-tight text-gray-800">{displayName}</div>
          <div className="truncate text-[11px] leading-tight text-gray-400">{u?.email}</div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          title="Sign out"
          aria-label="Sign out"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-500"
        >
          <SignOutIcon />
        </button>
      </div>
    </div>
  );

  /** The 64px icon rail the hamburger folds the sidebar into. */
  const renderRail = () => (
    <div className="flex h-full flex-col items-center">
      <div className="flex h-[60px] w-full shrink-0 items-center justify-center border-b border-gray-150">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Expand navigation"
          title="Expand navigation"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-lg text-gray-900 hover:bg-gray-100"
        >
          <Hamburger />
        </button>
      </div>
      <nav className="flex w-full flex-1 flex-col items-center gap-[3px] overflow-y-auto py-3" aria-label="Main">
        {NAV_GROUPS.map((group, gi) => (
          <Fragment key={group.key}>
            {gi > 0 && <span className="my-[7px] h-px w-6 shrink-0 bg-gray-150" aria-hidden="true" />}
            {group.items.map((item) => {
              const active = isActive(item.path);
              const dot = alertFor(item.path) !== null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  aria-label={item.label}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg transition-colors [&>svg]:h-[18px] [&>svg]:w-[18px] ${
                    active ? 'bg-primary-100 text-primary-500' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {item.icon}
                  {/* The rail has no room for a number - a red dot keeps the ALERT signal
                      visible instead of dropping it, since collapse state persists. Plain
                      counts are not shown here: they are information, not a summons. */}
                  {dot && <span className="absolute right-[5px] top-[5px] h-[7px] w-[7px] rounded-full bg-primary-500" aria-hidden="true" />}
                </Link>
              );
            })}
          </Fragment>
        ))}
      </nav>
      <div className="flex w-full shrink-0 flex-col items-center gap-2 border-t border-gray-150 py-3">
        <div title={u?.email} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-gray-800 text-[12px] font-bold leading-none text-white">
          {initials}
        </div>
        <button
          type="button"
          onClick={handleLogout}
          title="Sign out"
          aria-label="Sign out"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-primary-500"
        >
          <SignOutIcon />
        </button>
      </div>
    </div>
  );

  return (
    <AssistantRailContext.Provider value={rail}>
    <div className="min-h-screen">
      {/* ─── ⌘K Command Palette ─── */}
      <CommandPalette open={palette.open} onClose={palette.close} />

      {/* ─── "Add to Home Screen" ─── Every page renders Layout, so the offer reaches
          every screen from here. It returns null on the desktop, once installed, and
          inside the Capacitor shell - see InstallPrompt.tsx. */}
      <InstallPrompt />

      {/* ─── Desktop Sidebar ─── */}
      <aside
        className={`
          hidden md:flex flex-col fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200
          transition-[width] duration-200 ease-out
          ${collapsed ? 'w-16' : 'w-[246px]'}
        `}
      >
        {collapsed ? renderRail() : renderFullSidebar(false)}
      </aside>

      {/* ─── Mobile Drawer Overlay ─── (above the fullBleed map's z-[1000+] overlays and the rail's sheet) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[2100] flex">
          <div
            className="fixed inset-0 bg-gray-900/55"
            onClick={() => setMobileOpen(false)}
          />
          {/* `fixed inset-0` covers the status bar exactly as the top bar does, so the first
              nav row lands under the clock without the same inset. */}
          <div className="relative w-[280px] max-w-[84vw] bg-white shadow-lg flex flex-col animate-slide-in-left pt-[env(safe-area-inset-top)]">
            {renderFullSidebar(true)}
          </div>
        </div>
      )}

      {/* ─── Content column ─── */}
      <div
        className={`
          transition-[margin] duration-200 ease-out
          ${collapsed ? 'md:ml-16' : 'md:ml-[246px]'}
        `}
      >
        {/* ─── Top bar ─── */}
        {/*
          🚨 THE INSET IS ADDED TO THE HEIGHT, NOT TAKEN OUT OF IT. `viewport-fit=cover` draws
          the page behind the status bar, so this bar needs `pt-[env(safe-area-inset-top)]` -
          but on a fixed `h-[60px]` box that padding pushes the row DOWN inside the 60px and
          leaves 60 minus the inset for the controls (1px on an iPhone 15). The height carries
          the inset too, so the bar keeps its full 60px of content below the clock.
        */}
        <header className="sticky top-0 z-[1100] md:z-20 flex h-[calc(60px+env(safe-area-inset-top))] shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 pt-[env(safe-area-inset-top)] sm:px-[22px]">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="md:hidden flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-900 hover:bg-gray-100"
          >
            <Hamburger />
          </button>
          {/* BACK1 (the 152nd): his home-screen app has no browser Back. After the hamburger, so the menu button never
              moves under his thumb; it renders nothing on the first screen. */}
          <BackButton className="md:hidden" />
          <Link to="/dashboard" className="md:hidden flex items-center">
            <img src="/apex-logo.png" alt="Apex" className="block h-6 w-auto" />
          </Link>

          <button
            type="button"
            onClick={palette.openPalette}
            aria-label="Search properties, deals and contacts"
            className="hidden sm:flex h-[34px] w-full max-w-[340px] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-[11px] text-left text-[13px] text-gray-400 transition-colors hover:border-gray-300"
          >
            <SearchIcon />
            <span className="flex-1 truncate">Search properties, deals, contacts</span>
            <kbd className="rounded-sm border border-gray-200 bg-white px-[5px] py-[3px] font-sans text-[10.5px] font-medium leading-none text-gray-400">⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={palette.openPalette}
              aria-label="Search properties, deals and contacts"
              className="sm:hidden flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-900"
            >
              <SearchIcon />
            </button>
            {pageChat ? (
              <button
                type="button"
                onClick={pageChat}
                aria-label="Go to the chat"
                className="flex h-[34px] items-center gap-[7px] rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-semibold leading-none text-gray-800 transition-colors hover:border-gray-900"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
                Assistant
              </button>
            ) : (
              <button
                type="button"
                onClick={() => assistant.setOpen(!assistant.open)}
                aria-label={assistant.open ? 'Close the assistant' : 'Open the assistant'}
                aria-pressed={assistant.open}
                className={`flex h-[34px] items-center gap-[7px] rounded-lg border px-3 text-[12px] font-semibold leading-none transition-colors ${
                  assistant.open
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 bg-white text-gray-800 hover:border-gray-900'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${assistant.open ? 'bg-primary-400' : 'bg-primary-500'}`} aria-hidden="true" />
                Assistant
              </button>
            )}
          </div>
        </header>

        {fullBleed ? (
          <>
            {/* 🚨 fullBleed passes the flag: the rail must OVERLAY a Leaflet map, never resize
                its container. Leaflet measures once, and a resize without invalidateSize()
                gives grey tiles. */}
            {!pageChat && <AiRail fullBleed open={assistant.open} onOpenChange={assistant.setOpen} {...railBesideLetter} />}
            {/* CHAT-DUET: the map stops above the phone bar (`--tab-bar-space`, 0 where there is no bar). */}
            <main className="h-[calc(var(--vh-full)-60px-env(safe-area-inset-top,0px)-var(--tab-bar-space))] overflow-hidden">
              {children}
            </main>
          </>
        ) : (
          <div className="flex items-start">
            {/* CHAT-DUET: every page ends above the phone bar (`--tab-bar-space`, 0 where there is no bar). */}
            <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-[26px] pt-[22px] pb-[calc(22px+var(--tab-bar-space))]">
              {/* The content takes the width it is given (his ask, 2026-09-10: a 1440 cap left a blank band
                  beside every page on a 16-inch screen); 1920 only keeps an ultra-wide monitor readable. */}
              <div className="max-w-[1920px]">
                {children}
              </div>
            </main>
            {!pageChat && <AiRail open={assistant.open} onOpenChange={assistant.setOpen} {...railBesideLetter} />}
          </div>
        )}
      </div>

      {/* CHAT-DUET (his words, 2026-10-28): the phone bar - Chat, Tasks, Convo, My Deals, More. More opens the drawer. */}
      <PhoneTabBar onMore={() => setMobileOpen(true)} />

      {/* ⭐⭐ THE RECORDLESS LOI HOST (the 97th). Mounted HERE, beside the rail rather than
          inside it, because `AiRail` renders nothing while collapsed - a host living in there
          would deregister the moment he closed the panel, and the assistant would refuse to
          draft a letter for a reason invisible from the screen. It renders nothing until an
          action asks for it. */}
      <RecordlessLoiHost />
      {/* SLICE3 (the 125th): the Listing Proposal from chat with no record - see RecordlessProposalHost. */}
      <RecordlessProposalHost />
    </div>
    </AssistantRailContext.Provider>
  );
}
